import React, { useEffect, useMemo, useState } from "react";
import { Modal, Spin, Table } from "antd";
import dayjs from "dayjs";
import HighchartsReact from "highcharts-react-official";
import {
  getUsageActiveUsers,
  getUsageAuthFunnel,
  getUsageDropOffs,
  getUsageEngagementFunnel,
  getUsageInvestmentFunnel,
  getUsagePageViews,
  getUsageProducts,
  getUsageRetention,
  getUsageScreenTime,
  getUsageWidgetDetail,
  getUsageHotLeads,
} from "../../../apis/analyticsUsageApi";
import {
  barChartOptions,
  dataQualityDonut,
  donutOptions,
  formatDuration,
  formatCurrency,
  formatNumber,
  formatPct,
  formatProductLabel,
  mergeProductRows,
  funnelChartOptions,
  Highcharts,
  lineChartOptions,
  retentionHeatmapOptions,
  kpiDetailTrendOptions,
  sparklineOptions,
  THEME,
} from "./chartHelpers";
import AnalyticsChart from "./AnalyticsChart";
import DetailSection from "./DetailSection";
import WidgetDetailBreakdown from "./WidgetDetailBreakdown";
import HotLeadsPanel from "./HotLeadsPanel";
import { analyticsModalOverlayProps } from "./analyticsModalUtils";
import { filterSignupFunnelSteps } from "./funnelHelpers";
import { buildDetailExportSheet } from "./analyticsExport";
import WidgetExportButton from "./WidgetExportButton";
import AnalyticsHelpIcon from "./AnalyticsHelpIcon";

const DETAIL_META = {
  kpis: { title: "Key Metrics — Detailed", fetch: null },
  "active-users": { title: "Active Users Trend", fetch: getUsageActiveUsers },
  "installs-trend": { title: "Install Trend", fetch: null },
  "acquisition-sources": { title: "Acquisition Sources", fetch: null },
  platform: { title: "Platform Breakdown", fetch: null },
  attribution: { title: "Attribution Summary", fetch: null },
  auth: { title: "Auth & Registration", fetch: getUsageAuthFunnel },
  "signup-funnel": { title: "Signup Funnel & Step Breakdown", fetch: getUsageAuthFunnel },
  engagement: { title: "Engagement Metrics", fetch: getUsageEngagementFunnel },
  "attributed-installs": { title: "Attributed Installs", fetch: null },
  "investment-funnel": { title: "Investment Funnel", fetch: getUsageInvestmentFunnel },
  "top-screens": { title: "Top Screens", fetch: getUsageScreenTime },
  events: { title: "Events Explorer", fetch: getUsagePageViews },
  realtime: { title: "Real-time Activity", fetch: null },
  retention: { title: "Retention Cohorts", fetch: getUsageRetention },
  alerts: { title: "Alerts & Insights", fetch: null },
  errors: { title: "Top Errors", fetch: null },
  "data-quality": { title: "Data Quality", fetch: null },
  products: { title: "Product Performance", fetch: getUsageProducts },
  "campaign-roi": { title: "Campaign / Source ROI", fetch: null },
  "re-engagement": { title: "Re-engagement Opportunities", fetch: null },
  "hot-leads": { title: "Sales desk — Hot leads", fetch: getUsageHotLeads },
  "drop-offs": { title: "Drop-off Analysis", fetch: getUsageDropOffs },
};

const EventSparkline = ({ data }) => (
  <HighchartsReact
    highcharts={Highcharts}
    options={sparklineOptions(data || [0])}
    containerProps={{ style: { height: 32, width: 80 } }}
  />
);

const DetailBody = ({ detailKey, data, extra, params, compareEnabled, compLabel }) => {
  const d = data || {};

  switch (detailKey) {
    case "kpis": {
      const kpiDefs = [
        { key: "dau", label: "DAU" },
        { key: "wau", label: "WAU" },
        { key: "mau", label: "MAU" },
        { key: "page_views", label: "Page Views" },
        { key: "installs", label: "Installs" },
        { key: "signups", label: "Signups" },
        { key: "logins", label: "Logins" },
        { key: "investments", label: "Investments" },
        { key: "investment_amount", label: "Investment Amount", format: formatCurrency },
      ];
      const formatKpiValue = (key, value) => {
        const def = kpiDefs.find((item) => item.key === key);
        return def?.format ? def.format(value) : formatNumber(value);
      };
      const rows = kpiDefs.map(({ key, label }) => {
        const k = d.kpis?.[key] || {};
        return {
          key,
          metric: label.toUpperCase(),
          current: k.value,
          previous: k.previous,
          change_pct: k.change_pct,
        };
      });
      const trendCharts = kpiDefs.filter(({ key }) => (d.kpis?.[key]?.sparkline?.length ?? 0) > 1);
      return (
        <>
          <p className="analytics-detail-desc">
            Daily, weekly, and monthly active users plus core conversion metrics for the selected period.
            {compareEnabled && compLabel ? ` ${compLabel}.` : ""}
          </p>
          <Table
            size="small"
            pagination={false}
            className="analytics-table"
            columns={[
              { title: "Metric", dataIndex: "metric", key: "m" },
              {
                title: "Current",
                dataIndex: "current",
                key: "c",
                render: (v, row) => formatKpiValue(row.key, v),
              },
              {
                title: "Previous",
                dataIndex: "previous",
                key: "p",
                render: (v, row) => (v != null ? formatKpiValue(row.key, v) : "—"),
              },
              {
                title: "Change",
                dataIndex: "change_pct",
                key: "ch",
                render: (v) => (v != null ? `${v > 0 ? "+" : ""}${v.toFixed(1)}%` : "—"),
              },
            ]}
            dataSource={rows}
          />
          {trendCharts.length > 0 && (
            <>
              <h5>Period trends</h5>
              <div className="analytics-detail-charts">
                {trendCharts.map(({ key, label, format: formatVal = formatNumber }) => {
                  const k = d.kpis?.[key] || {};
                  return (
                    <div key={key} className="analytics-detail-mini-chart">
                      <div className="analytics-detail-mini-chart__head">
                        <span className="analytics-detail-mini-chart__label">{label}</span>
                        <span className="analytics-detail-mini-chart__value">{formatVal(k.value)}</span>
                      </div>
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={kpiDetailTrendOptions(k.sparkline)}
                        containerProps={{ style: { height: 88, width: "100%" } }}
                      />
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      );
    }

    case "active-users": {
      const fromFetch = extra?.series?.length
        ? {
            dau: extra.granularity === "week"
              ? extra.series.map((x) => ({ date: x.date, dau: x.active_users }))
              : extra.series.map((x) => ({ date: x.date, dau: x.active_users ?? x.dau ?? 0 })),
          }
        : null;
      const trend = extra?.active_users || fromFetch || d.active_users_trend || {};
      const dau = trend.dau || [];
      const wau = trend.wau || [];
      const mau = trend.mau || [];
      const categories = dau.length
        ? dau.map((x) => dayjs(x.date).format("MMM D"))
        : wau.map((x) => dayjs(x.date).format("MMM D"));
      const dauSeries = dau.length ? dau.map((x) => x.dau) : [];
      const wauSeries = wau.length ? wau.map((x) => x.wau) : [];
      return (
        <>
          <p className="analytics-detail-desc">
            DAU, WAU, and MAU over the selected date range. Weekly view uses WAU series from PostHog.
          </p>
          {categories.length ? (
            <HighchartsReact
              options={lineChartOptions({
                categories,
                yTitle: "Users",
                series: [
                  ...(dauSeries.length ? [{ name: "DAU", data: dauSeries, color: THEME.primary }] : []),
                  ...(wauSeries.length ? [{ name: "WAU", data: wauSeries, color: THEME.success }] : []),
                  ...(mau.length
                    ? [{
                        name: "MAU",
                        data: dau.length
                          ? dau.map((_, i) => mau[Math.min(i, mau.length - 1)]?.mau ?? 0)
                          : mau.map((x) => x.mau),
                        color: THEME.purple,
                        dashStyle: "ShortDot",
                      }]
                    : []),
                ],
              })}
            />
          ) : (
            <p className="analytics-empty">No active-user trend for this period</p>
          )}
        </>
      );
    }

    case "installs-trend": {
      const trend = d.installs_trend || {};
      const daily = trend.daily || [];
      const weekly = trend.weekly || [];
      const useWeekly = weekly.length > daily.length;
      const categories = useWeekly
        ? weekly.map((x) => dayjs(x.date).format("MMM D"))
        : daily.map((x) => dayjs(x.date).format("MMM D"));
      const seriesData = useWeekly
        ? weekly.map((x) => x.installs)
        : daily.map((x) => x.installs);
      const sourceEvent = d.installs_meta?.source_event;
      const sourceLabel =
        sourceEvent === "appsflyer_install_conversion_received"
          ? "AppsFlyer install conversion events"
          : "Attribution detected events";

      return (
        <>
          <p className="analytics-detail-desc">
            App installs over the selected date range ({sourceLabel}).
            {d.installs_meta?.unique_installs != null
              ? ` ${formatNumber(d.installs_meta.unique_installs)} unique devices/users in period.`
              : ""}
          </p>
          {categories.length ? (
            <AnalyticsChart
              options={lineChartOptions({
                categories,
                yTitle: "Installs",
                series: [{ name: "Installs", data: seriesData, color: THEME.purple }],
              })}
              height={320}
            />
          ) : (
            <p className="analytics-empty">No install events for this period</p>
          )}
        </>
      );
    }

    case "acquisition-sources":
      return (
        <>
          <p className="analytics-detail-desc">User acquisition breakdown by marketing source.</p>
          <div className="analytics-detail-split">
            <HighchartsReact options={donutOptions(d.acquisition?.sources || [], "Sources")} />
            <Table
              size="small"
              pagination={false}
              className="analytics-table"
              columns={[
                { title: "Source", dataIndex: "label", key: "l" },
                { title: "Users", dataIndex: "users", key: "u", render: formatNumber },
                { title: "%", dataIndex: "pct", key: "p", render: formatPct },
              ]}
              dataSource={(d.acquisition?.sources || []).map((r, i) => ({ ...r, key: i }))}
            />
          </div>
        </>
      );

    case "platform":
      return (
        <>
          <p className="analytics-detail-desc">Install and session distribution by iOS vs Android.</p>
          <HighchartsReact options={donutOptions(d.acquisition?.platforms || [], "Platform")} />
          <Table
            size="small"
            pagination={false}
            className="analytics-table analytics-detail-table-top"
            columns={[
              { title: "Platform", dataIndex: "label", key: "l" },
              { title: "Users", dataIndex: "users", key: "u", render: formatNumber },
              { title: "%", dataIndex: "pct", key: "p", render: formatPct },
            ]}
            dataSource={(d.acquisition?.platforms || []).map((r, i) => ({ ...r, key: i }))}
          />
        </>
      );

    case "attribution":
      return (
        <>
          <p className="analytics-detail-desc">
            AppsFlyer attribution summary — paid vs organic and channel mix.
          </p>
          <Table
            size="small"
            pagination={false}
            className="analytics-table"
            columns={[
              { title: "Source", dataIndex: "label", key: "l" },
              { title: "Count", dataIndex: "count", key: "c", render: formatNumber },
              { title: "%", dataIndex: "pct", key: "p", render: formatPct },
            ]}
            dataSource={(d.acquisition?.summary || []).map((r, i) => ({ ...r, key: i }))}
          />
        </>
      );

    case "auth": {
      const signupFunnel = filterSignupFunnelSteps(extra?.signup_funnel || extra?.signup?.funnel || []);
      const loginFunnel = extra?.login_funnel || [];
      return (
        <>
          <p className="analytics-detail-desc">
            Sign-up funnel uses signup_flow_started → password → OTP → signup_completed. Login funnel uses SignIn screen events.
          </p>
          <Table
            size="small"
            pagination={false}
            className="analytics-table"
            columns={[
              { title: "Metric", dataIndex: "metric", key: "m" },
              { title: "Count", dataIndex: "cur", key: "c", render: formatNumber },
              {
                title: "Change %",
                dataIndex: "change_pct",
                key: "ch",
                render: (v) => (v != null ? `${v.toFixed(1)}%` : "—"),
              },
            ]}
            dataSource={(d.auth_performance_table || []).map((r) => ({ ...r, key: r.key }))}
          />
          {signupFunnel.length > 0 && (
            <div className="analytics-detail-table-top">
              <h5>Signup funnel</h5>
              <Table
                size="small"
                pagination={false}
                className="analytics-table"
                columns={[
                  { title: "Step", dataIndex: "step", key: "s" },
                  { title: "Users", dataIndex: "count", key: "c", render: formatNumber },
                  { title: "Conversion", dataIndex: "conversion_rate", key: "cv", render: formatPct },
                  { title: "Drop-off", dataIndex: "dropoff_rate", key: "d", render: formatPct },
                ]}
                dataSource={signupFunnel.map((r, i) => ({ ...r, key: i }))}
              />
              <HighchartsReact options={funnelChartOptions(signupFunnel, { light: true })} />
            </div>
          )}
          {loginFunnel.length > 0 && (
            <div className="analytics-detail-table-top">
              <h5>Login funnel</h5>
              <HighchartsReact options={funnelChartOptions(loginFunnel, { light: true })} />
            </div>
          )}
        </>
      );
    }

    case "signup-funnel": {
      const signup = extra?.signup || d.signup_analytics || {};
      const summary = signup.summary || d.signup_funnel?.summary || {};
      const mainFunnel = filterSignupFunnelSteps(signup.funnel || d.signup_funnel?.funnel || []);
      const extendedFunnel = filterSignupFunnelSteps(
        signup.extended_funnel || d.signup_funnel?.extended_funnel || []
      );
      return (
        <>
          <p className="analytics-detail-desc">
            Mobile signup from inv-fe: flow started, per-step views/completions, abandons, and failures.
          </p>
          <div className="analytics-detail-kv-grid">
            {[
              ["Flow started", formatNumber(summary.flow_started)],
              ["Completed", formatNumber(summary.completed)],
              ["Abandoned", formatNumber(summary.abandoned)],
              ["Failed", formatNumber(summary.failed)],
              ["Completion rate", formatPct(summary.completion_rate || 0)],
              ["Drop-off (no register)", formatNumber(summary.drop_off_without_registration)],
            ].map(([label, val]) => (
              <div key={label}>
                <span>{label}</span>
                <strong>{val}</strong>
              </div>
            ))}
          </div>
          {mainFunnel.length > 0 && (
            <>
              <h5>Recommended funnel (started → password → OTP → completed)</h5>
              <Table
                size="small"
                pagination={false}
                className="analytics-table"
                columns={[
                  { title: "Step", dataIndex: "step", key: "s" },
                  { title: "Users", dataIndex: "count", key: "c", render: formatNumber },
                  { title: "Conversion", dataIndex: "conversion_rate", key: "cv", render: formatPct },
                  { title: "Drop-off", dataIndex: "dropoff_rate", key: "d", render: formatPct },
                ]}
                dataSource={mainFunnel.map((r, i) => ({ ...r, key: i }))}
              />
              <HighchartsReact options={funnelChartOptions(mainFunnel, { light: true })} />
            </>
          )}
          {extendedFunnel.length > 0 && (
            <div className="analytics-detail-table-top">
              <h5>Extended funnel (includes email step)</h5>
              <HighchartsReact options={funnelChartOptions(extendedFunnel, { light: true })} />
            </div>
          )}
          {(signup.step_views || []).length > 0 && (
            <>
              <h5>Step views <span className="analytics-detail-event-key">(signup_step_viewed)</span></h5>
              <Table
                size="small"
                pagination={false}
                className="analytics-table"
                columns={[
                  { title: "Step", dataIndex: "label", key: "l" },
                  { title: "Views", dataIndex: "views", key: "v", render: formatNumber },
                  { title: "Users", dataIndex: "users", key: "u", render: formatNumber },
                ]}
                dataSource={(signup.step_views || []).map((r, i) => ({ ...r, key: i }))}
              />
            </>
          )}
          {(signup.step_completions || []).length > 0 && (
            <>
              <h5>Step completions <span className="analytics-detail-event-key">(signup_step_completed)</span></h5>
              <Table
                size="small"
                pagination={false}
                className="analytics-table"
                columns={[
                  { title: "Step", dataIndex: "label", key: "l" },
                  { title: "Completions", dataIndex: "count", key: "c", render: formatNumber },
                ]}
                dataSource={(signup.step_completions || []).map((r, i) => ({ ...r, key: i }))}
              />
            </>
          )}
          {(signup.abandon_reasons || []).length > 0 && (
            <>
              <h5>Abandon reasons <span className="analytics-detail-event-key">(signup_flow_abandoned)</span></h5>
              <Table
                size="small"
                pagination={false}
                className="analytics-table"
                columns={[
                  { title: "Reason", dataIndex: "reason", key: "r" },
                  { title: "Count", dataIndex: "count", key: "c", render: formatNumber },
                ]}
                dataSource={(signup.abandon_reasons || []).map((r, i) => ({ ...r, key: i }))}
              />
            </>
          )}
        </>
      );
    }

    case "engagement": {
      const funnel = extra?.funnel || extra?.steps || [];
      const eng = d.engagement_kpis || {};
      return (
        <>
          <p className="analytics-detail-desc">Session depth, bounce rate, and feature usage.</p>
          <div className="analytics-detail-kv-grid">
            {[
              ["Avg session", formatDuration(eng.avg_session_duration_sec?.value ?? d.engagement?.avg_session_duration_sec)],
              ["Screens / session", eng.screens_per_session?.value ?? d.engagement?.screens_per_session],
              ["Bounce rate", formatPct(eng.bounce_rate?.value ?? d.engagement?.bounce_rate)],
              ["API failure rate", formatPct(eng.api_failure_rate?.value ?? d.engagement?.api_failure_rate)],
            ].map(([label, val]) => (
              <div key={label}>
                <span>{label}</span>
                <strong>{typeof val === "number" ? formatNumber(val) : val}</strong>
              </div>
            ))}
          </div>
          {funnel.length > 0 && (
            <>
              <h5>Engagement funnel</h5>
              <HighchartsReact options={funnelChartOptions(funnel, { light: true })} />
            </>
          )}
        </>
      );
    }

    case "attributed-installs": {
      const items = d.attributed_installs || [];
      return (
        <>
          <p className="analytics-detail-desc">Install counts attributed to each marketing channel.</p>
          {items.length ? (
            <>
              <HighchartsReact
                options={barChartOptions({
                  categories: items.map((i) => i.channel),
                  data: items.map((i) => i.count),
                  color: THEME.purple,
                })}
              />
              <Table
                size="small"
                pagination={false}
                className="analytics-table analytics-detail-table-top"
                columns={[
                  { title: "Channel", dataIndex: "channel", key: "c" },
                  { title: "Installs", dataIndex: "count", key: "n", render: formatNumber },
                ]}
                dataSource={items.map((r, i) => ({ ...r, key: i }))}
              />
            </>
          ) : (
            <p className="analytics-empty">No attributed installs in this period</p>
          )}
        </>
      );
    }

    case "investment-funnel": {
      const steps = extra?.funnel || d.investment_funnel?.funnel || [];
      return (
        <>
          <p className="analytics-detail-desc">
            Investment journey from product view to completion. Overall conversion:{" "}
            {formatPct(d.investment_funnel?.overall_conversion_rate || extra?.overall_conversion_rate || 0)}.
          </p>
          <Table
            size="small"
            pagination={false}
            className="analytics-table"
            columns={[
              { title: "Step", dataIndex: "step", key: "s" },
              { title: "Users", dataIndex: "count", key: "c", render: formatNumber },
              { title: "Conversion", dataIndex: "conversion_rate", key: "cv", render: formatPct },
              { title: "Drop-off", dataIndex: "dropoff_rate", key: "d", render: formatPct },
            ]}
            dataSource={steps.map((r, i) => ({ ...r, key: i }))}
          />
          <div className="analytics-detail-table-top">
            <HighchartsReact options={funnelChartOptions(steps, { light: true })} />
          </div>
        </>
      );
    }

    case "top-screens": {
      const fromBreakdown = (extra?.breakdown || []).map((row) => ({
        screen: row.label || row.feature_name || row.page_name || "unknown",
        views: row.sessions ?? row.views ?? 0,
        users: row.users ?? row.unique_users ?? 0,
        avg_time_sec: row.avg_time_seconds ?? Math.round((row.avg_time_ms || 0) / 1000),
      }));
      const screens = extra?.screens?.length ? extra.screens : fromBreakdown.length ? fromBreakdown : d.top_screens || [];
      return (
        <>
          <p className="analytics-detail-desc">Screen views, unique users, and average time on screen.</p>
          <Table
            size="small"
            pagination={{ pageSize: 15, size: "small" }}
            className="analytics-table"
            columns={[
              { title: "Screen", dataIndex: "screen", key: "s" },
              { title: "Views", dataIndex: "views", key: "v", render: formatNumber },
              { title: "Users", dataIndex: "users", key: "u", render: formatNumber },
              { title: "Avg time", dataIndex: "avg_time_sec", key: "t", render: formatDuration },
            ]}
            dataSource={screens.map((r, i) => ({ ...r, key: i }))}
          />
        </>
      );
    }

    case "events":
      return (
        <>
          <p className="analytics-detail-desc">
            {d.event_stats?.types_count ?? 0} event types · {formatNumber(d.event_stats?.total_count)} total events.
          </p>
          <Table
            size="small"
            pagination={{ pageSize: 20, size: "small" }}
            className="analytics-table"
            columns={[
              { title: "Category", dataIndex: "category", key: "c" },
              { title: "Events", dataIndex: "count", key: "n", render: formatNumber },
              {
                title: "Trend",
                dataIndex: "sparkline",
                key: "t",
                render: (v) => <EventSparkline data={v} />,
              },
            ]}
            dataSource={(d.events_overview || []).map((r, i) => ({ ...r, key: i }))}
          />
          {extra?.page_views?.length > 0 && (
            <>
              <h5>Page / screen views</h5>
              <Table
                size="small"
                pagination={false}
                className="analytics-table"
                columns={[
                  { title: "Screen", dataIndex: "screen", key: "s" },
                  { title: "Views", dataIndex: "views", key: "v", render: formatNumber },
                ]}
                dataSource={extra.page_views.map((r, i) => ({ ...r, key: i }))}
              />
            </>
          )}
        </>
      );

    case "realtime":
      return (
        <>
          <p className="analytics-detail-desc">Most recent events from PostHog (last ~30 minutes).</p>
          <ul className="analytics-realtime-feed analytics-realtime-feed--detail">
            {(d.realtime_activity || []).map((ev, i) => (
              <li key={`${ev.timestamp}-${i}`}>
                <div>
                  <strong>{ev.label || ev.event}</strong>
                  <span>{dayjs(ev.timestamp).format("MMM D, HH:mm:ss")}</span>
                  {ev.properties && (
                    <code className="analytics-detail-event-props">
                      {JSON.stringify(ev.properties).slice(0, 120)}
                    </code>
                  )}
                </div>
                <span className={`platform-tag platform-tag--${ev.platform}`}>{ev.platform}</span>
              </li>
            ))}
            {!d.realtime_activity?.length && <li className="analytics-empty">No recent events</li>}
          </ul>
        </>
      );

    case "retention": {
      const cohorts = extra?.cohorts || d.retention?.cohorts || [];
      return (
        <>
          <p className="analytics-detail-desc">Weekly cohort retention — % of users returning each week.</p>
          <HighchartsReact options={retentionHeatmapOptions(cohorts, "Week")} />
        </>
      );
    }

    case "alerts":
      return (
        <>
          <p className="analytics-detail-desc">Automated insights based on metric changes and data quality.</p>
          <ul className="analytics-alerts">
            {(d.alerts || []).map((a, i) => (
              <li key={i} className={`analytics-alert analytics-alert--${a.severity}`}>
                <i
                  className={`fa-solid fa-${
                    a.severity === "error"
                      ? "circle-xmark"
                      : a.severity === "warning"
                        ? "triangle-exclamation"
                        : "circle-check"
                  }`}
                />
                <div>
                  <strong>{a.title}</strong>
                  <p>{a.message}</p>
                </div>
              </li>
            ))}
            {!d.alerts?.length && <p className="analytics-empty">No alerts for this period</p>}
          </ul>
        </>
      );

    case "errors":
      return (
        <>
          <p className="analytics-detail-desc">Top application errors captured from mobile events.</p>
          <Table
            size="small"
            pagination={{ pageSize: 15, size: "small" }}
            className="analytics-table"
            columns={[
              { title: "Error", dataIndex: "message", key: "m", ellipsis: true },
              { title: "Count", dataIndex: "count", key: "c", width: 80 },
              { title: "Users", dataIndex: "users", key: "u", width: 80 },
              {
                title: "Last seen",
                dataIndex: "last_seen",
                key: "l",
                width: 130,
                render: (v) => (v ? dayjs(v).format("MMM D HH:mm") : "—"),
              },
            ]}
            dataSource={(d.errors || []).map((r, i) => ({ ...r, key: i }))}
            locale={{ emptyText: "No errors" }}
          />
        </>
      );

    case "data-quality": {
      const dq = d.data_quality || {};
      return (
        <>
          <p className="analytics-detail-desc">
            Event ingestion quality — good events vs dropped or duplicate records.
          </p>
          <HighchartsReact
            options={dataQualityDonut({
              good: dq.good_events,
              dropped: dq.dropped,
              duplicate: dq.duplicate,
              goodPct: dq.good_pct,
            })}
          />
          <div className="analytics-dq-legend analytics-detail-table-top">
            <span>
              <i className="dot dot--ok" /> Good: {formatNumber(dq.good_events)} ({formatPct(dq.good_pct || 0)})
            </span>
            <span>
              <i className="dot dot--err" /> Dropped: {formatNumber(dq.dropped)}
            </span>
            <span>
              <i className="dot dot--warn" /> Duplicate: {formatNumber(dq.duplicate)}
            </span>
          </div>
        </>
      );
    }

    case "products": {
      const fromDashboard = d.top_products || [];
      const viewed = extra?.product_viewed || [];
      const cta = extra?.cta_clicked || [];
      const submitted = extra?.investment_submitted || [];
      const map = new Map();
      fromDashboard.forEach((row) => {
        const product = formatProductLabel(row.product);
        const key = product.toLowerCase();
        if (!map.has(key)) map.set(key, { ...row, product, views: 0, cta_clicks: 0, investments: 0 });
        const item = map.get(key);
        item.views += row.views || 0;
        item.cta_clicks += row.cta_clicks || 0;
        item.investments += row.investments || 0;
      });
      if (!fromDashboard.length && (viewed.length || cta.length || submitted.length)) {
        const ensure = (name) => {
          const product = formatProductLabel(name);
          const key = product.toLowerCase();
          if (!map.has(key)) map.set(key, { product, views: 0, cta_clicks: 0, investments: 0 });
          return map.get(key);
        };
        viewed.forEach((row) => {
          ensure(row.product_name).views += row.count || 0;
        });
        cta.forEach((row) => {
          ensure(row.product_name).cta_clicks += row.count || 0;
        });
        submitted.forEach((row) => {
          ensure(row.product_name).investments += row.count || 0;
        });
      }
      const products = mergeProductRows(Array.from(map.values())).map((row) => ({
        ...row,
        conv_rate: row.views > 0 ? (row.investments || 0) / row.views : row.conv_rate || 0,
      }));
      return (
        <>
          <p className="analytics-detail-desc">Product views, CTA clicks, and investments by product.</p>
          {products.length ? (
            <Table
              size="small"
              pagination={false}
              className="analytics-table"
              columns={[
                { title: "Product", dataIndex: "product", key: "p", ellipsis: true, render: formatProductLabel },
                { title: "Views", dataIndex: "views", key: "v", render: formatNumber },
                { title: "CTA Clicks", dataIndex: "cta_clicks", key: "c", render: formatNumber },
                { title: "Investments", dataIndex: "investments", key: "i", render: formatNumber },
                { title: "Conv. Rate", dataIndex: "conv_rate", key: "r", render: formatPct },
              ]}
              dataSource={products.map((r, i) => ({ ...r, key: i }))}
            />
          ) : (
            <p className="analytics-empty">No product data for this period</p>
          )}
        </>
      );
    }

    case "campaign-roi": {
      const roi = d.campaign_roi || {};
      const rows = roi.rows || [];
      return (
        <>
          <p className="analytics-detail-desc">
            Attributed users, signups, and investments by campaign / source.
          </p>
          {rows.length ? (
            <Table
              size="small"
              pagination={false}
              className="analytics-table"
              columns={[
                { title: "Source", dataIndex: "source", key: "s", ellipsis: true },
                { title: "Attributed Users", dataIndex: "attributed_users", key: "u", render: formatNumber },
                { title: "Signups", dataIndex: "signups", key: "sg", render: formatNumber },
                { title: "Investments", dataIndex: "investments", key: "i", render: formatNumber },
                { title: "Conv. Rate", dataIndex: "conv_rate", key: "c", render: formatPct },
              ]}
              dataSource={rows.map((r, i) => ({ ...r, key: i }))}
            />
          ) : (
            <p className="analytics-empty">
              {roi.warning || "No campaign ROI data for this period"}
            </p>
          )}
        </>
      );
    }

    case "re-engagement": {
      const rows = d.reengagement || [];
      return (
        <>
          <p className="analytics-detail-desc">
            Users who abandoned key flows or went inactive — candidates for win-back campaigns.
          </p>
          {rows.length ? (
            <Table
              size="small"
              pagination={false}
              className="analytics-table"
              columns={[
                { title: "Cohort", dataIndex: "cohort", key: "c" },
                { title: "Users", dataIndex: "users", key: "u", render: formatNumber },
                { title: "Suggested action", dataIndex: "suggested_action", key: "a", ellipsis: true },
              ]}
              dataSource={rows.map((r, i) => ({ ...r, key: i }))}
            />
          ) : (
            <p className="analytics-empty">No re-engagement cohorts for this period</p>
          )}
        </>
      );
    }

    case "hot-leads": {
      const hotLeads = extra?.rows ? extra : d.hot_leads || {};
      return (
        <>
          <p className="analytics-detail-desc">
            Individual users who tapped invest or abandoned the flow without submitting. Call and email actions
            require a linked CRM account.
          </p>
          <HotLeadsPanel hotLeads={hotLeads} limit={null} showHeader={false} />
        </>
      );
    }

    case "drop-offs": {
      const items = extra?.drop_offs || extra?.items || [];
      const signupAbandoned = extra?.signup_abandoned || [];
      const signupFailed = extra?.signup_failures || [];
      const signupBack = extra?.signup_back_navigation || [];
      const hasSignupData = signupAbandoned.length > 0 || signupFailed.length > 0 || signupBack.length > 0;
      const dropoffColumns = [
        { title: "Step / Screen", dataIndex: "step", key: "s", width: "40%" },
        { title: "Entered", dataIndex: "entered", key: "e", render: formatNumber, align: "right" },
        { title: "Dropped", dataIndex: "dropped", key: "d", render: formatNumber, align: "right" },
        { title: "Drop-off %", dataIndex: "dropoff_rate", key: "r", render: formatPct, align: "right" },
      ];

      return (
        <>
          <p className="analytics-detail-desc">
            Investment and signup drop-offs — abandoned flows, back navigation, and failures.
          </p>
          {hasSignupData && (
            <DetailSection title="Signup funnel" icon="fa-solid fa-user-xmark">
              {signupAbandoned.length > 0 && (
                <div className="analytics-detail-subsection">
                  <h5>Abandons by step</h5>
                  <Table
                    size="small"
                    pagination={false}
                    className="analytics-table analytics-detail-table"
                    columns={[
                      { title: "Step", dataIndex: "label", key: "l" },
                      { title: "Count", dataIndex: "count", key: "c", render: formatNumber, align: "right", width: 100 },
                      {
                        title: "Avg time on step",
                        dataIndex: "avg_time_on_step_ms",
                        key: "t",
                        render: (v) => (v ? formatDuration(v / 1000) : "—"),
                        align: "right",
                        width: 140,
                      },
                    ]}
                    dataSource={signupAbandoned.map((r, i) => ({ ...r, key: i }))}
                  />
                </div>
              )}
              {signupFailed.length > 0 && (
                <div className="analytics-detail-subsection">
                  <h5>Failures by step</h5>
                  <Table
                    size="small"
                    pagination={false}
                    className="analytics-table analytics-detail-table"
                    columns={[
                      { title: "Step", dataIndex: "label", key: "l" },
                      { title: "Count", dataIndex: "count", key: "c", render: formatNumber, align: "right", width: 100 },
                    ]}
                    dataSource={signupFailed.map((r, i) => ({ ...r, key: i }))}
                  />
                </div>
              )}
              {signupBack.length > 0 && (
                <div className="analytics-detail-subsection">
                  <h5>Back navigation</h5>
                  <Table
                    size="small"
                    pagination={false}
                    className="analytics-table analytics-detail-table"
                    columns={[
                      { title: "Step", dataIndex: "label", key: "l" },
                      { title: "Count", dataIndex: "count", key: "c", render: formatNumber, align: "right", width: 100 },
                    ]}
                    dataSource={signupBack.map((r, i) => ({ ...r, key: i }))}
                  />
                </div>
              )}
            </DetailSection>
          )}
          {items.length > 0 ? (
            <DetailSection title="Investment flow" icon="fa-solid fa-chart-line">
              <Table
                size="small"
                pagination={false}
                className="analytics-table analytics-detail-table"
                columns={dropoffColumns}
                dataSource={items.map((r, i) => ({ ...r, key: i }))}
              />
            </DetailSection>
          ) : (
            !hasSignupData && <p className="analytics-empty">No drop-off data for this period</p>
          )}
        </>
      );
    }

    default:
      return <p className="analytics-empty">No detail view available.</p>;
  }
};

const AnalyticsDetailModal = ({ open, detailKey, onClose, data, params, compareEnabled, compLabel, exportSheets = {} }) => {
  const [loading, setLoading] = useState(false);
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [extra, setExtra] = useState(null);
  const [audience, setAudience] = useState(null);

  const meta = useMemo(() => DETAIL_META[detailKey] || { title: "Details", fetch: null }, [detailKey]);

  useEffect(() => {
    if (!open || !detailKey) {
      setExtra(null);
      setAudience(null);
      return undefined;
    }

    let cancelled = false;
    const fetchFn = DETAIL_META[detailKey]?.fetch;

    setAudienceLoading(true);
    setAudience(null);
    getUsageWidgetDetail({ ...params, widget: detailKey })
      .then((res) => {
        if (!cancelled) setAudience(res?.result || null);
      })
      .catch(() => {
        if (!cancelled) setAudience(null);
      })
      .finally(() => {
        if (!cancelled) setAudienceLoading(false);
      });

    if (!fetchFn) {
      setExtra(null);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    setExtra(null);

    fetchFn(params)
      .then((res) => {
        if (!cancelled) setExtra(res?.result || res || {});
      })
      .catch(() => {
        if (!cancelled) setExtra({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, detailKey, params]);

  const showAudience = audienceLoading || Boolean(audience);

  const detailExportSheet = useMemo(() => {
    if (!detailKey) return null;
    return (
      exportSheets[detailKey] ||
      buildDetailExportSheet(detailKey, data, extra) ||
      null
    );
  }, [detailKey, exportSheets, data, extra]);

  const hotLeadsExportRows = useMemo(() => {
    if (detailKey !== "hot-leads") return null;
    const hotLeads = extra?.rows ? extra : data?.hot_leads || {};
    return hotLeads.rows ?? [];
  }, [detailKey, data, extra]);

  return (
    <Modal
      open={open}
      title={
        <span className="analytics-detail-modal__title">
          {meta.title}
          <AnalyticsHelpIcon helpKey={detailKey} />
          {(detailExportSheet || detailKey === "hot-leads") && (
            <WidgetExportButton
              sheet={detailExportSheet}
              slug={detailKey}
              widgetKey={detailKey === "hot-leads" ? undefined : detailKey}
              exportParams={detailKey === "hot-leads" ? undefined : params}
              hotLeadsRows={hotLeadsExportRows}
              className="analytics-export-btn--modal"
            />
          )}
        </span>
      }
      onCancel={onClose}
      footer={null}
      width={960}
      destroyOnClose
      className="analytics-detail-modal"
      {...analyticsModalOverlayProps}
      classNames={{
        body: "analytics-detail-modal__body",
        header: "analytics-detail-modal__header",
        content: "analytics-detail-modal__content-wrap",
      }}
    >
      {audienceLoading ? (
        <div className="analytics-widget-audience analytics-widget-audience--loading">
          <Spin size="small" />
          <span>Loading audience breakdown…</span>
        </div>
      ) : (
        audience && <WidgetDetailBreakdown audience={audience} onNavigate={onClose} />
      )}

      <div
        className={`analytics-detail-modal__main${showAudience ? "" : " analytics-detail-modal__main--solo"}`}
      >
        {loading && DETAIL_META[detailKey]?.fetch ? (
          <div className="analytics-detail-loading">
            <Spin tip="Loading detailed data..." />
          </div>
        ) : (
          <DetailBody
            detailKey={detailKey}
            data={data}
            extra={extra}
            params={params}
            compareEnabled={compareEnabled}
            compLabel={compLabel}
          />
        )}
      </div>
    </Modal>
  );
};

export { DETAIL_META };
export default AnalyticsDetailModal;
